'use client';

import { useGetAllExportsXmpMetadataQuery } from '@frameio/graphql-client/generated';
import { usePluginFeatures } from '../../shared/hooks/usePluginFeatures';
import {
  useExportMetadataEventsSubscription,
  usePremiereProjectEventsSubscription,
} from '../gql';

/**
 * Keeps sequence export metadata in sync across the integrations app.
 *
 * This component fetches XMP metadata for all exported sequences and keeps it
 * fresh by subscribing to Premiere project events. The cached data is used to
 * determine which assets are linked to sequences (Browse tab, Sequences tab,
 * etc.)
 *
 * - Fetches fresh data on mount (`cache-and-network`)
 * - Subscribes to project open/close events to trigger refetch
 * - Updates Apollo cache, automatically notifying all consuming components
 */
export function SequencesMetadataSync() {
  const { canHandleExportMetadataEvents } = usePluginFeatures();
  const { refetch } = useGetAllExportsXmpMetadataQuery({
    context: { resolveWithUXP: true },
    fetchPolicy: 'cache-and-network',
  });

  usePremiereProjectEventsSubscription({
    context: { resolveWithUXP: true },
    onData: (data) => {
      const evtType = data.data?.data?.premiereProjectEvents?.__typename;

      if (
        evtType === 'PremiereProjectOpenedPayload' ||
        evtType === 'PremiereProjectClosedPayload'
      ) {
        refetch();
      }
    },
  });

  useExportMetadataEventsSubscription({
    skip: !canHandleExportMetadataEvents,
    onData: (subscriptionData) => {
      const event = subscriptionData.data?.data?.exportMetadataEvents;
      const evtType = event?.__typename;

      if (
        evtType === 'ExportMetadataChangedPayload' &&
        event?.reason &&
        ['LINKED_SEQUENCE_ADDED', 'LINKED_SEQUENCE_DELETED'].includes(
          event.reason
        )
      ) {
        refetch();
      }
    },
    context: {
      resolveWithUXP: true,
    },
  });

  return null;
}
